if (!customElements.get('share-button')) {
  customElements.define(
    'share-button',
    class ShareButton extends HTMLElement {
      constructor() {
        super();

        this.elements = {
          shareButton: this.querySelector('button.share-button__native'),
          shareSummary: this.querySelector('summary'),
          closeButton: this.querySelector('.share-button__close'),
          successMessage: this.querySelector('[id^="ShareMessage"]'),
          urlInput: this.querySelector('input[id^="ShareUrl"], input[data-share-url-input]'),
          odysseyTrigger: this.querySelector('[data-odyssey-share-trigger]'),
          odysseyHint: this.querySelector('[data-odyssey-share-hint]'),
          odysseyAction: this.querySelector('[data-odyssey-share-action]'),
        };

        this.urlToShare = this.elements.urlInput
          ? this.elements.urlInput.value
          : document.location.href;

        if (this.elements.odysseyTrigger) {
          this.initOdysseyShare();
          return;
        }

        this.mainDetailsToggle = this.querySelector('details');
        if (!this.mainDetailsToggle) return;

        this.content = this.mainDetailsToggle.querySelector('summary')?.nextElementSibling;
        this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
        this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));

        if (navigator.share && this.elements.shareButton) {
          this.mainDetailsToggle.setAttribute('hidden', '');
          this.elements.shareButton.classList.remove('hidden');
          this.elements.shareButton.addEventListener('click', () => {
            navigator.share({ url: this.urlToShare, title: document.title });
          });
        } else {
          this.mainDetailsToggle.addEventListener('toggle', this.toggleDetails.bind(this));
          this.mainDetailsToggle
            .querySelector('.share-button__copy')
            ?.addEventListener('click', this.copyToClipboard.bind(this));
          this.mainDetailsToggle
            .querySelector('.share-button__close')
            ?.addEventListener('click', this.close.bind(this));
        }
      }

      initOdysseyShare() {
        const trigger = this.elements.odysseyTrigger;
        const hint = this.elements.odysseyHint;
        const action = this.elements.odysseyAction;
        if (hint && !hint.dataset.defaultText) {
          hint.dataset.defaultText = hint.textContent.trim();
        }
        if (action && !action.dataset.defaultText) {
          action.dataset.defaultText = action.textContent.trim();
        }

        trigger.addEventListener('click', async () => {
          const url = this.urlToShare;
          if (navigator.share) {
            try {
              await navigator.share({ url, title: document.title });
              return;
            } catch (err) {
              if (err && err.name === 'AbortError') return;
            }
          }
          try {
            await navigator.clipboard.writeText(url);
            this.showOdysseyCopied();
          } catch (_) {
            if (hint) hint.textContent = 'Copy from address bar';
          }
        });
      }

      showOdysseyCopied() {
        const hint = this.elements.odysseyHint;
        const action = this.elements.odysseyAction;
        if (!hint) return;
        hint.textContent = 'Link copied to clipboard';
        hint.classList.add('is-copied');
        if (action) {
          action.textContent = 'Copied';
          action.classList.add('is-copied');
        }
        window.clearTimeout(this._odysseyCopiedTimer);
        this._odysseyCopiedTimer = window.setTimeout(() => {
          hint.textContent = hint.dataset.defaultText || 'Tap to copy product link';
          hint.classList.remove('is-copied');
          if (action) {
            action.textContent = action.dataset.defaultText || 'Copy';
            action.classList.remove('is-copied');
          }
        }, 2400);
      }

      onFocusOut() {
        window.setTimeout(() => {
          if (!this.contains(document.activeElement)) this.close();
        });
      }

      onToggle() {
        if (!this.content || !this.mainDetailsToggle) return;
        if (!this.animations) this.animations = this.content.getAnimations?.() || [];

        if (this.mainDetailsToggle.hasAttribute('open')) {
          this.animations.forEach((animation) => animation.play());
        } else {
          this.animations.forEach((animation) => animation.cancel());
        }
      }

      toggleDetails() {
        if (!this.mainDetailsToggle?.open || !this.elements.successMessage) return;
        this.elements.successMessage.classList.add('hidden');
        this.elements.successMessage.textContent = '';
        this.elements.closeButton?.classList.add('hidden');
        this.elements.shareSummary?.focus();
      }

      copyToClipboard() {
        if (!this.elements.urlInput || !this.elements.successMessage) return;
        navigator.clipboard.writeText(this.elements.urlInput.value).then(() => {
          this.elements.successMessage.classList.remove('hidden');
          this.elements.successMessage.textContent = window.accessibilityStrings.shareSuccess;
          this.elements.closeButton?.classList.remove('hidden');
          this.elements.closeButton?.focus();
        });
      }

      close() {
        if (!this.mainDetailsToggle) return;
        this.mainDetailsToggle.removeAttribute('open');
        this.mainDetailsToggle.querySelector('summary')?.setAttribute('aria-expanded', false);
      }

      updateUrl(url) {
        this.urlToShare = url;
        if (this.elements.urlInput) this.elements.urlInput.value = url;
      }
    }
  );
}
